import React, { useEffect, useState } from 'react';

import { RemediationsbyCategory } from '@ariksa/compliance-policies/api';
import { Box, Stack, Center, useDisclosure, Text } from '@chakra-ui/react';
import { forEach, isEmpty, map } from 'lodash';
import { useDeepCompareEffect } from 'react-use';
import { useRemediationService } from 'services/ComplianceService/Remediation';
import { useAlertService } from 'services/Notification/useAlerts';
import { colorOpacity, customTheme } from 'theme';

import {
  DetailsIdentifier,
  renderCloudIcon,
  renderSeverityBar,
  renderTableHeaderWithLoader,
  StackedHeader,
  Tag,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { ActionButton } from 'components/DataEntry';
import { DetachIcon, RemediateIcon } from 'components/Icons';
import { Drawer } from 'components/Overlay';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { AttachRemediationToAlert } from 'containers/PolicyHub/Remediation/Components/RemediationTable/AttachRemediationToAlert';
import { toTitleCase } from 'utils/string';

interface Props {
  row: RemediationsbyCategory;
  isOpen: boolean;
  onClose();
}

export const AlertsAssociatedToRemediation: React.FC<Props> = props => {
  const { row, isOpen, onClose } = props;
  const detachRemediation = useDisclosure();
  const { rulesAttachedToRemediation } = useRemediationService();
  const { alertsByRuleIdSummary } = useAlertService();
  const [currentRecord, setCurrentRecord] = useState<Record<string, any>>({});
  const [alertsSummary, setAlertsSummary] = useState({});
  const { getResourceAlias } = useResourceType();

  useDeepCompareEffect(() => {
    rulesAttachedToRemediation.sync({
      remediationId: row?.id,
    });
  }, [rulesAttachedToRemediation]);

  useDeepCompareEffect(() => {
    !isEmpty(rulesAttachedToRemediation.data) &&
      alertsByRuleIdSummary.sync({
        ruleIdSummaryRequest: {
          rule_ids: map(
            rulesAttachedToRemediation.data,
            o => o.rule_info.config_id,
          ),
        },
      });
  }, [alertsByRuleIdSummary, rulesAttachedToRemediation.data]);

  useEffect(() => {
    const data = {};
    forEach(alertsByRuleIdSummary.data, o => (data[o.rule_id] = o.alert_count));
    setAlertsSummary(data);
  }, [alertsByRuleIdSummary.data]);

  const renderAction = ({ row }) => {
    return (
      <Center
        onClick={e => {
          e.stopPropagation();
        }}
      >
        <ActionButton
          onClick={() => {
            detachRemediation.onOpen();
            setCurrentRecord(row);
          }}
          isDisabled={row?.created_by === 'Ariksa'}
          icon={<DetachIcon color="red" />}
          label="Detach remediation from this policy"
        />
      </Center>
    );
  };

  const columns = [
    {
      header: <Box pl={12}>Alert Description</Box>,
      accessor: 'rule_info.alert_description',
      render: ({ row }) => (
        <WithResourceIcon
          resourceType={row?.rule_info?.resource_type}
          color="primary"
        >
          <Text whiteSpace="wrap" noOfLines={2}>
            {row?.rule_info?.alert_description}
          </Text>
        </WithResourceIcon>
      ),
    },
    {
      header: 'Category',
      accessor: 'rule_info.alert_category',
      align: 'left',
      render: ({ value }) => toTitleCase(value),
    },
    {
      header: 'Severity',
      accessor: 'rule_info.severity',
      align: 'left',
      render: ({ value }) =>
        renderSeverityBar({ value: value, isInline: false }),
    },
    {
      header: (
        <Center>
          {renderTableHeaderWithLoader(
            'Issues',
            alertsByRuleIdSummary.isLoading,
          )}
        </Center>
      ),
      render: ({ row }) => alertsSummary[row.rule_info.config_id] ?? 0,
    },
    {
      header: 'Actions',
      accessor: 'actions',
      render: renderAction,
    },
  ];

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      styles={{ drawer: { size: '2xl' } }}
      header={
        <StackedHeader
          upper={row.description}
          lower={row?.name}
          icon={<RemediateIcon />}
        />
      }
      body={
        <Stack spacing={3} w="full" h="full" pt={4} px={4}>
          {/*<Box>
            <FormFieldBox bg={colorMap.primary(30)} border="none">
              {row?.command}
            </FormFieldBox>
          </Box>*/}
          <DetailsIdentifier
            label="Cloud"
            value={renderCloudIcon({ provider: row?.cloud })}
          />
          <DetailsIdentifier
            label="Resource Type"
            value={getResourceAlias(row?.resource_type!)}
          />
          <DetailsIdentifier
            label="Context"
            value={
              <Tag
                label={row.created_by !== 'Ariksa' ? 'CUSTOM' : 'NONE'}
                styles={{
                  label: {
                    color:
                      row.created_by !== 'Ariksa'
                        ? 'white'
                        : customTheme.colors.gray[300],
                  },
                  tag: {
                    py: 0.5,
                    border: 'none',
                    borderRadius: 4,
                    px: row.created_by === 'Ariksa' ? 4 : 2,
                    bg:
                      row.created_by !== 'Ariksa'
                        ? 'primary'
                        : colorOpacity(customTheme.colors.gray[300], 0.1),
                  },
                }}
              />
            }
            align="center"
          />
          <Box flex={1} pt={2}>
            <Table
              data={rulesAttachedToRemediation.data ?? []}
              columns={columns}
              isLoading={rulesAttachedToRemediation.isLoading}
            />
          </Box>
          {detachRemediation.isOpen && (
            <AttachRemediationToAlert
              isOpen={detachRemediation.isOpen}
              onClose={detachRemediation.onClose}
              row={row}
              actionType="Detach"
              alertRuleId={currentRecord?.policy_id}
            />
          )}
        </Stack>
      }
    />
  );
};
